import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plane, Briefcase, Heart, MapPin, Globe } from 'lucide-react';

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/9e8b8adb-4702-43d4-a386-2eaf3df07374/images/543075ead14fb84b02e7344d90d965ec4cb236b345428732cf10d9a17dd8ae3a.png";

const services = [
  {
    icon: Plane,
    title: 'Airport Chauffeur Service',
    image: 'https://static.prod-images.emergentagent.com/jobs/9e8b8adb-4702-43d4-a386-2eaf3df07374/images/f76ebf89d6d946372af396cbe6513ec461315688796b47cbb80a123d198b2880.png',
    description: 'Travel should feel effortless from the moment you leave your door. Our airport chauffeur service provides seamless, on-time transport to and from Pearson and Billy Bishop, with real-time coordination, luggage assistance, and a calm, comfortable ride. Whether you\u2019re departing for business, returning home, or welcoming a VIP guest, we ensure every journey begins and ends with precision and professionalism.',
    cta: 'Book Airport Chauffeur',
  },
  {
    icon: Briefcase,
    title: 'Corporate Travel',
    image: 'https://static.prod-images.emergentagent.com/jobs/9e8b8adb-4702-43d4-a386-2eaf3df07374/images/db1208a8833fe49d89b4d5ed0b33e46ced015b63c841abc4abaed3f741d41d92.png',
    description: 'Your time is valuable \u2014 and your travel should reflect that. Toronto Royale Chauffeur provides discreet, professional transport for executives, founders, and corporate teams. From back-to-back meetings to full-day itineraries, our chauffeurs deliver a composed, reliable experience that supports your schedule and enhances your presence. Cross-border corporate travel is also available for clients requiring seamless movement between Canada and the United States.',
    cta: 'Book Corporate Travel',
  },
  {
    icon: Heart,
    title: 'Private Travel',
    image: 'https://static.prod-images.emergentagent.com/jobs/9e8b8adb-4702-43d4-a386-2eaf3df07374/images/fd3a25b7ffaaaece1c6a6cfb33a4dc1997e3e4d606e70dee1b59515804c5922a.png',
    description: 'Whether it\u2019s an evening out, a family celebration, or a personal itinerary, our private chauffeur service offers comfort, discretion, and a smooth, uninterrupted experience tailored to your plans. Enjoy the freedom to move at your own pace while we handle the driving, timing, and coordination with care and attention.',
    cta: 'Book Private Travel',
  },
  {
    icon: MapPin,
    title: 'Long-Distance Routes',
    image: 'https://static.prod-images.emergentagent.com/jobs/9e8b8adb-4702-43d4-a386-2eaf3df07374/images/3c519abde95136c0da03d9bb88bbfb53b46978e066f03fdc11de0b5dd00fe5ad.png',
    description: 'Travel beyond the GTA with ease and comfort. Our long-distance chauffeur service provides a premium alternative to traditional transport, offering spacious SUVs, smooth rides, and flexible routing to destinations such as Niagara Falls, Waterloo, London, Kingston, and more. We also support long-range, cross-border travel for clients requiring direct transport to U.S. destinations.',
    cta: 'Request Long-Distance Quote',
  },
  {
    icon: Globe,
    title: 'Cross-Border Chauffeur Service',
    image: 'https://static.prod-images.emergentagent.com/jobs/9e8b8adb-4702-43d4-a386-2eaf3df07374/images/a6861c9f0a6d4dafef8e2f1bdc60ac39c72850c59d49f9fa430a2d621d55c06c.png',
    description: 'Move seamlessly between Canada and the United States with our premium cross-border chauffeur service. Whether you\u2019re traveling for business, leisure, or family commitments, we provide a smooth, secure, and fully coordinated journey across the border. Our chauffeurs manage timing, documentation checkpoints, and route planning to ensure a stress-free experience from door to door.',
    highlights: [
      'Corporate travel between Toronto and U.S. cities',
      'Airport transfers to Buffalo, Detroit, or other U.S. hubs',
      'Family or personal travel requiring direct cross-border transport',
      'Clients who prefer private, uninterrupted travel over commercial options',
    ],
    cta: 'Request Cross-Border Chauffeur',
  },
];

export const ChauffeurServices = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) e.target.classList.add('fade-in');
      }),
      { threshold: 0.05, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.fade-element').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div data-testid="chauffeur-services-page" style={{ backgroundColor: '#0A0A0A' }}>

      {/* ── HERO ── */}
      <section data-testid="services-areas-hero" className="relative overflow-hidden flex items-center justify-center" style={{ minHeight: '100vh' }}>
        <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url(${HERO_IMG})`,
          filter: 'brightness(0.42) saturate(0.9) contrast(1.08)',
          scale: '1.05',
          animation: 'heroZoom 25s ease-in-out infinite alternate',
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.2) 35%, rgba(0,0,0,0.15) 55%, rgba(0,0,0,0.75) 100%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 35%, rgba(194,168,120,0.06) 0%, transparent 55%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(135deg, rgba(0,0,0,0.3) 0%, transparent 40%, transparent 60%, rgba(0,0,0,0.3) 100%)',
        }} />
        <div className="grain-overlay absolute inset-0" style={{ opacity: 0.04 }} />

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto" style={{ paddingTop: '180px', paddingBottom: '180px' }}>
          <div className="fade-element" style={{ animationDelay: '0.2s' }}>
            <div className="gold-divider mx-auto" style={{ marginBottom: '32px', opacity: 0.7 }} />
            <p style={{ fontSize: '11px', color: 'rgba(194,168,120,0.55)', letterSpacing: '5px', textTransform: 'uppercase', fontWeight: 300, marginBottom: '36px' }}>
              Toronto Royale Chauffeur
            </p>
          </div>
          <div className="fade-element" style={{ animationDelay: '0.4s' }}>
            <h1 data-testid="services-areas-hero-heading" className="font-serif" style={{
              fontSize: 'clamp(40px, 5.5vw, 68px)', fontWeight: 500, color: '#FAFAFA',
              marginBottom: '32px', lineHeight: 1.08, textShadow: '0 2px 40px rgba(0,0,0,0.5)',
            }}>
              Chauffeur Services Across Toronto and the GTA
            </h1>
          </div>
          <div className="fade-element" style={{ animationDelay: '0.6s' }}>
            <p style={{ fontSize: '18px', color: 'rgba(220,220,220,0.65)', fontWeight: 300, lineHeight: 1.8, maxWidth: '520px', margin: '0 auto 56px' }}>
              Precision, professionalism, and luxury transport for every occasion.
            </p>
          </div>
          <div className="fade-element" style={{ animationDelay: '0.8s' }}>
            <button
              data-testid="hero-cta-btn"
              onClick={() => navigate('/chauffeur/book')}
              style={{
                backgroundColor: '#C2A878', color: '#0A0A0A', padding: '15px 40px',
                fontSize: '12px', fontWeight: 500, letterSpacing: '1.2px', textTransform: 'uppercase',
                border: 'none', cursor: 'pointer', transition: 'all 400ms ease',
                boxShadow: '0 4px 24px rgba(194,168,120,0.15)',
              }}
              onMouseEnter={(e) => { e.target.style.backgroundColor = '#D4BA8A'; e.target.style.boxShadow = '0 6px 32px rgba(194,168,120,0.25)'; }}
              onMouseLeave={(e) => { e.target.style.backgroundColor = '#C2A878'; e.target.style.boxShadow = '0 4px 24px rgba(194,168,120,0.15)'; }}
            >
              Request a Chauffeur
            </button>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0" style={{ height: '160px', background: 'linear-gradient(to top, #0A0A0A, transparent)' }} />
      </section>

      {/* ── SERVICE BLOCKS ── */}
      {services.map((service, i) => {
        const isEven = i % 2 === 0;
        return (
          <section
            key={service.title || service.name || i}
            data-testid={`service-area-card-${i}`}
            className="relative overflow-hidden"
            style={{ backgroundColor: '#0A0A0A' }}
          >
            <div className="max-w-7xl mx-auto">
              <div className={`grid grid-cols-1 lg:grid-cols-2`} style={{ minHeight: '700px' }}>

                {/* IMAGE SIDE */}
                <div
                  className={`relative overflow-hidden fade-element ${isEven ? '' : 'lg:order-2'}`}
                  style={{ minHeight: '400px' }}
                >
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-[1200ms] ease-out"
                    style={{
                      backgroundImage: `url(${service.image})`,
                      filter: 'brightness(0.65) saturate(0.9) contrast(1.05)',
                    }}
                    onMouseEnter={(e) => { e.target.style.transform = 'scale(1.04)'; }}
                    onMouseLeave={(e) => { e.target.style.transform = 'scale(1)'; }}
                  />
                  {/* Depth overlays on image */}
                  <div className="absolute inset-0" style={{
                    background: isEven
                      ? 'linear-gradient(90deg, transparent 50%, rgba(10,10,10,0.95) 100%)'
                      : 'linear-gradient(270deg, transparent 50%, rgba(10,10,10,0.95) 100%)',
                  }} />
                  <div className="absolute inset-0" style={{
                    background: 'linear-gradient(180deg, rgba(10,10,10,0.3) 0%, transparent 30%, transparent 70%, rgba(10,10,10,0.3) 100%)',
                  }} />
                  {/* Gold corner accent */}
                  <div className="absolute" style={{
                    ...(isEven
                      ? { bottom: '40px', right: '40px' }
                      : { bottom: '40px', left: '40px' }),
                    width: '48px', height: '48px',
                    borderBottom: '1px solid rgba(194,168,120,0.2)',
                    ...(isEven
                      ? { borderRight: '1px solid rgba(194,168,120,0.2)' }
                      : { borderLeft: '1px solid rgba(194,168,120,0.2)' }),
                  }} />
                </div>

                {/* CONTENT SIDE */}
                <div
                  className={`relative flex items-center fade-element ${isEven ? '' : 'lg:order-1'}`}
                  style={{ padding: 'clamp(48px, 6vw, 96px)' }}
                >
                  {/* Ambient glow */}
                  <div className="absolute inset-0" style={{
                    background: isEven
                      ? 'radial-gradient(ellipse at 30% 50%, rgba(194,168,120,0.03) 0%, transparent 50%)'
                      : 'radial-gradient(ellipse at 70% 50%, rgba(194,168,120,0.03) 0%, transparent 50%)',
                  }} />

                  <div className="relative z-10" style={{ maxWidth: '520px' }}>
                    {/* Icon */}
                    <div style={{
                      width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      backgroundColor: 'rgba(194,168,120,0.04)', border: '1px solid rgba(194,168,120,0.1)',
                      marginBottom: '32px',
                    }}>
                      <service.icon size={20} strokeWidth={1.5} style={{ color: '#C2A878', opacity: 0.65 }} />
                    </div>

                    {/* Thin gold line */}
                    <div style={{ width: '32px', height: '1px', backgroundColor: 'rgba(194,168,120,0.25)', marginBottom: '28px' }} />

                    <h2 className="font-serif" style={{
                      fontSize: 'clamp(26px, 3vw, 38px)', fontWeight: 400, color: '#F5F5F5',
                      marginBottom: '28px', lineHeight: 1.2,
                    }}>
                      {service.title}
                    </h2>

                    <p style={{
                      fontSize: '15px', color: 'rgba(204,204,204,0.55)', fontWeight: 300,
                      lineHeight: 2, marginBottom: service.highlights ? '28px' : '40px',
                    }}>
                      {service.description}
                    </p>

                    {/* Highlights for Cross-Border */}
                    {service.highlights && (
                      <div style={{ marginBottom: '40px' }}>
                        <p style={{
                          fontSize: '11px', color: 'rgba(194,168,120,0.5)', letterSpacing: '2px',
                          textTransform: 'uppercase', fontWeight: 300, marginBottom: '18px',
                        }}>
                          Ideal for
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          {service.highlights.map((item) => (
                            <div key={item} className="flex items-center" style={{ gap: '12px' }}>
                              <div style={{
                                width: '4px', height: '4px', backgroundColor: 'rgba(194,168,120,0.35)',
                                borderRadius: '50%', flexShrink: 0,
                              }} />
                              <span style={{ fontSize: '14px', color: 'rgba(225,225,225,0.8)', fontWeight: 300 }}>
                                {item}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <button
                      data-testid={`service-cta-${i}`}
                      onClick={() => navigate('/chauffeur/book')}
                      className="group"
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '10px',
                        backgroundColor: 'transparent', color: '#C2A878',
                        padding: '13px 30px', fontSize: '12px', fontWeight: 400,
                        letterSpacing: '1px', textTransform: 'uppercase',
                        border: '1px solid rgba(194,168,120,0.2)',
                        cursor: 'pointer', transition: 'all 400ms ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(194,168,120,0.06)';
                        e.currentTarget.style.borderColor = 'rgba(194,168,120,0.45)';
                        e.currentTarget.style.boxShadow = '0 4px 24px rgba(194,168,120,0.08)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.borderColor = 'rgba(194,168,120,0.2)';
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      {service.cta}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Section divider */}
            {i < services.length - 1 && (
              <div style={{
                position: 'relative', width: '100%', height: '1px',
                background: 'linear-gradient(90deg, transparent, rgba(194,168,120,0.08) 20%, rgba(194,168,120,0.08) 80%, transparent)',
              }} />
            )}
          </section>
        );
      })}

      {/* ── FINAL CTA ── */}
      <section data-testid="services-areas-cta" className="relative overflow-hidden" style={{ minHeight: '600px' }}>
        <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url(${HERO_IMG})`,
          filter: 'brightness(0.15) saturate(0.5)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(180deg, rgba(10,10,10,0.97) 0%, rgba(10,10,10,0.55) 35%, rgba(10,10,10,0.55) 65%, rgba(10,10,10,0.97) 100%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 50%, rgba(194,168,120,0.06) 0%, transparent 45%)',
        }} />
        <div className="grain-overlay absolute inset-0" style={{ opacity: 0.04 }} />

        <div className="max-w-3xl mx-auto text-center relative z-10" style={{ padding: '14rem 1.5rem' }}>
          <div className="fade-element">
            <div className="gold-divider mx-auto" style={{ marginBottom: '32px', opacity: 0.6 }} />
            <h2 className="font-serif" style={{
              fontSize: 'clamp(32px, 4.5vw, 54px)', fontWeight: 500, color: '#FAFAFA',
              marginBottom: '24px', lineHeight: 1.12, textShadow: '0 2px 30px rgba(0,0,0,0.4)',
            }}>
              Ready to Reserve Your Chauffeur?
            </h2>
            <p style={{
              fontSize: '17px', color: 'rgba(220,220,220,0.5)', fontWeight: 300,
              lineHeight: 1.8, maxWidth: '500px', margin: '0 auto 52px',
            }}>
              Experience the comfort, professionalism, and precision of Toronto Royale Chauffeur.
            </p>
            <button
              data-testid="services-areas-cta-btn"
              onClick={() => navigate('/chauffeur/book')}
              style={{
                backgroundColor: '#C2A878', color: '#0A0A0A', padding: '15px 44px',
                fontSize: '12px', fontWeight: 500, letterSpacing: '1.2px', textTransform: 'uppercase',
                border: 'none', cursor: 'pointer', transition: 'all 400ms ease',
                boxShadow: '0 4px 24px rgba(194,168,120,0.15)',
              }}
              onMouseEnter={(e) => { e.target.style.backgroundColor = '#D4BA8A'; e.target.style.boxShadow = '0 6px 32px rgba(194,168,120,0.25)'; }}
              onMouseLeave={(e) => { e.target.style.backgroundColor = '#C2A878'; e.target.style.boxShadow = '0 4px 24px rgba(194,168,120,0.15)'; }}
            >
              Request a Chauffeur
            </button>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes heroZoom {
          0% { transform: scale(1.05); }
          100% { transform: scale(1.12); }
        }
      `}</style>
    </div>
  );
};
